<script lang="ts" module>
  /** L'aperçu de geste ne se joue qu'une fois par session, pas à chaque
   *  retour sur la Boîte : passé la première fois, c'est du clignotement. */
  let peekPlayed = false;
</script>

<script lang="ts">
  /**
   * La Boîte — l'écran d'atterrissage.
   *
   * Ligne d'une seule ligne de texte, sur une grille de quatre colonnes :
   * point de non-lu · expéditeur (88px) · objet · queue. Pas d'extrait :
   * à 430px la colonne d'objet fait ~270px, l'objet la remplit déjà, et y
   * ajouter un extrait ne laissait plus voir que le début de l'objet.
   * Pas de chip de label non plus — le rail de lentilles est le point
   * d'entrée par label.
   */

  import { onMount, onDestroy } from "svelte";
  import { gmailState, type InboxRow, type FilterKind } from "../gmail/store.svelte";
  import { authState } from "../auth/store.svelte";
  import {
    loadInbox,
    loadMore,
    archiveThread,
    markRead,
    markUnread,
  } from "../gmail/sync";
  import { startSignIn } from "../auth/oauth";
  import { swipeRow } from "../gestures/swipeRow";
  import { haptic } from "../gestures/haptic";
  import { labelsState, displayLabel } from "../gmail/labels.svelte";
  import { getHeader, parseFrom } from "../gmail/headers";
  import { toggleLater, isLater, flagsState } from "../gmail/threadFlags.svelte";
  import { showToast } from "../snackbar/store.svelte";
  import Icon, { type IconName } from "./shared/Icon.svelte";
  import { openSnooze } from "./snoozeStore.svelte";
  import { openLabelSheet } from "./labelSheetStore.svelte";
  import { openReplyCompose } from "../gmail/compose";
  import {
    settingsState,
    SWIPE_ACTION_LABELS,
    type SwipeAction,
  } from "../settings.svelte";

  interface Props {
    /** Révèle la barre de recherche (portée par App). */
    onSearch: () => void;
  }
  let { onSearch }: Props = $props();

  // === Groupage ============================================================
  type Grouping = "date" | "company" | "none";
  const GROUPING_KEY = "mailfox:inbox-grouping";
  const GROUP_LABELS: Record<Grouping, string> = {
    date: "Date",
    company: "Société",
    none: "Aucun",
  };

  function readGrouping(): Grouping {
    try {
      const v = localStorage.getItem(GROUPING_KEY);
      if (v === "date" || v === "company" || v === "none") return v;
    } catch {
      // Stockage indisponible : on repart du défaut, ce n'est pas une panne.
    }
    return "date";
  }

  let grouping = $state<Grouping>(readGrouping());

  function cycleGrouping() {
    const order: Grouping[] = ["date", "company", "none"];
    grouping = order[(order.indexOf(grouping) + 1) % order.length];
    haptic("light");
    try {
      localStorage.setItem(GROUPING_KEY, grouping);
    } catch {
      // Idem : le mode reste valable pour la session.
    }
  }

  // === Lentilles ===========================================================
  // Les quatre premières mappent sur `gmailState.filter.kind` (donc sur la
  // requête Gmail) ; « À traiter » et « En attente » sont des vues locales
  // au-dessus de `flagsState` — ces fils sont masqués de la Boîte, et sans
  // ces deux lentilles ils n'étaient tout simplement plus atteignables.
  type LocalView = "none" | "later" | "snoozed";
  let localView = $state<LocalView>("none");

  interface Lens {
    id: string;
    label: string;
    count: number;
    on: boolean;
    pick: () => void;
  }

  const activeLabelId = $derived(
    gmailState.filter.labelIds.find((id) => id !== "INBOX") ?? null,
  );

  function pickKind(kind: FilterKind) {
    if (gmailState.filter.kind === kind && localView === "none" && !activeLabelId) return;
    haptic("light");
    localView = "none";
    gmailState.filter = { kind, labelIds: ["INBOX"], query: null };
    void loadInbox();
  }

  function pickLocal(v: LocalView) {
    haptic("light");
    localView = v;
  }

  function pickLabel(labelId: string) {
    haptic("light");
    localView = "none";
    gmailState.filter = { kind: "inbox", labelIds: [labelId], query: null };
    void loadInbox();
  }

  const laterCount = $derived(
    gmailState.rows.filter((r) => flagsState.byId[r.thread.id]?.later === true).length,
  );
  const snoozedCount = $derived.by(() => {
    const now = Date.now();
    return gmailState.rows.filter((r) => {
      const u = flagsState.byId[r.thread.id]?.snoozedUntil;
      return !!u && u > now;
    }).length;
  });
  const unreadCount = $derived(gmailState.rows.filter((r) => r.unread).length);

  const lenses = $derived.by<Lens[]>(() => {
    const k = gmailState.filter.kind;
    const plain = localView === "none" && !activeLabelId;
    const out: Lens[] = [
      { id: "all", label: "Tous", count: gmailState.rows.length,
        on: plain && k === "inbox", pick: () => pickKind("inbox") },
      { id: "unread", label: "Non lus", count: unreadCount,
        on: plain && k === "unread", pick: () => pickKind("unread") },
      { id: "later", label: "À traiter", count: laterCount,
        on: localView === "later", pick: () => pickLocal("later") },
      { id: "snoozed", label: "En attente", count: snoozedCount,
        on: localView === "snoozed", pick: () => pickLocal("snoozed") },
      { id: "sent", label: "Envoyés", count: 0,
        on: plain && k === "sent", pick: () => pickKind("sent") },
    ];
    for (const l of labelsState.splits) {
      out.push({
        id: l.id,
        label: displayLabel(l.name),
        count: gmailState.rows.filter((r) =>
          r.thread.messages.some((m) => m.labelIds?.includes(l.id)),
        ).length,
        on: activeLabelId === l.id,
        pick: () => pickLabel(l.id),
      });
    }
    return out;
  });

  // === Sélection multiple ==================================================
  let selectMode = $state(false);
  let selected = $state<Set<string>>(new Set());

  function toggleSelectMode() {
    haptic("light");
    selectMode = !selectMode;
    if (!selectMode) selected = new Set();
  }

  function toggleSelected(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    selected = next;
  }

  function bulkArchive() {
    const ids = [...selected];
    if (ids.length === 0) return;
    haptic("medium");
    for (const id of ids) void archiveThread(id);
    showToast({ message: `${ids.length} fil${ids.length > 1 ? "s" : ""} archivé${ids.length > 1 ? "s" : ""}` });
    selected = new Set();
    selectMode = false;
  }

  function bulkRead() {
    const ids = [...selected];
    if (ids.length === 0) return;
    haptic("medium");
    for (const id of ids) void markRead(id);
    selected = new Set();
    selectMode = false;
  }

  // === Lifecycle ===========================================================
  let cleanupListeners: (() => void) | null = null;

  onMount(() => {
    if (gmailState.rows.length === 0) {
      void loadInbox();
    } else {
      const stale =
        !gmailState.lastSyncAt || Date.now() - gmailState.lastSyncAt > 60_000;
      if (stale) void loadInbox();
    }

    const onFocus = () => {
      if (document.visibilityState === "visible") void loadInbox();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    cleanupListeners = () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };

    // Aperçu de geste : la première ligne se décale de 84px puis revient.
    // Ça enseigne le balayage sans tutoriel, et ne se joue qu'une fois.
    if (!peekPlayed) {
      peekPlayed = true;
      peek = true;
      peekTimer = setTimeout(() => (peek = false), 1600);
    }
  });

  let peek = $state(false);
  let peekTimer: ReturnType<typeof setTimeout> | undefined;

  // Tick minute pour que les heures relatives ne se figent pas.
  let tick = $state(0);
  let tickTimer: ReturnType<typeof setInterval> | undefined;
  onMount(() => {
    tickTimer = setInterval(() => (tick += 1), 60_000);
  });
  onDestroy(() => {
    cleanupListeners?.();
    clearInterval(tickTimer);
    clearTimeout(peekTimer);
  });

  // === Helpers =============================================================
  /** HH:MM le jour même, jour abrégé sous 7 j, date dans l'année, +année au-delà. */
  function relativeDate(unixMs: number): string {
    tick; // dépendance explicite : force le recalcul à chaque minute
    const now = new Date();
    const d = new Date(unixMs);
    const sameDay =
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear();
    if (sameDay) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    if (now.getTime() - unixMs < 7 * 86_400_000) {
      return d.toLocaleDateString("fr-FR", { weekday: "short" }).replace(".", "");
    }
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    }
    return d.toLocaleDateString("fr-FR", {
      day: "numeric", month: "short", year: "2-digit",
    });
  }

  function lastFromMe(row: InboxRow): boolean {
    const me = authState.email?.toLowerCase();
    if (!me) return false;
    const last = row.thread.messages[row.thread.messages.length - 1];
    if (!last) return false;
    return parseFrom(getHeader(last, "From")).email.toLowerCase() === me;
  }

  function senderDisplay(row: InboxRow): string {
    if (lastFromMe(row)) return "Moi";
    return row.fromName?.trim() || row.fromEmail || "(inconnu)";
  }

  function hasAttachment(row: InboxRow): boolean {
    for (const m of row.thread.messages) {
      for (const p of m.payload?.parts ?? []) {
        if (p.filename && p.filename.length > 0) return true;
      }
    }
    return false;
  }

  /** Clé de groupe par date, dans l'ordre où on lit une boîte. */
  function dateBucket(unixMs: number): string {
    const d = new Date(unixMs);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    if (unixMs >= startOfToday) return "Aujourd'hui";
    if (unixMs >= startOfToday - 86_400_000) return "Hier";
    if (unixMs >= startOfToday - 7 * 86_400_000) return "Cette semaine";
    if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString("fr-FR", { month: "long" });
    }
    return d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  }

  /** Clé de groupe par société : le domaine de l'expéditeur. */
  function companyBucket(row: InboxRow): string {
    if (lastFromMe(row)) return "Envoyés";
    const at = (row.fromEmail ?? "").split("@")[1];
    return at ? at.toLowerCase() : "Sans domaine";
  }

  // === Lignes visibles =====================================================
  const visibleRows = $derived.by(() => {
    const now = Date.now();
    return gmailState.rows.filter((r) => {
      const f = flagsState.byId[r.thread.id];
      const snoozed = !!f?.snoozedUntil && f.snoozedUntil > now;
      if (localView === "later") return f?.later === true;
      if (localView === "snoozed") return snoozed;
      // Vue normale : ce qui est reporté ou mis de côté sort de la Boîte.
      return !snoozed && f?.later !== true;
    });
  });

  interface Group {
    key: string;
    rows: InboxRow[];
  }

  const groups = $derived.by<Group[]>(() => {
    const rows = visibleRows;
    if (grouping === "none") {
      return rows.length > 0 ? [{ key: "Tous les fils", rows }] : [];
    }
    const order: string[] = [];
    const by = new Map<string, InboxRow[]>();
    for (const r of rows) {
      const k = grouping === "date" ? dateBucket(r.lastDate) : companyBucket(r);
      if (!by.has(k)) {
        by.set(k, []);
        order.push(k);
      }
      by.get(k)!.push(r);
    }
    return order.map((k) => ({ key: k, rows: by.get(k)! }));
  });

  const headerTitle = $derived.by(() => {
    if (localView === "later") return "À traiter";
    if (localView === "snoozed") return "En attente";
    if (activeLabelId) {
      const lbl = labelsState.byId.get(activeLabelId);
      if (lbl) return displayLabel(lbl.name);
    }
    switch (gmailState.filter.kind) {
      case "all": return "Tous les mails";
      case "sent": return "Envoyés";
      case "unread": return "Non lus";
      default: return "Boîte";
    }
  });

  const emptyMessage = $derived.by(() => {
    if (gmailState.filter.query) return `Aucun résultat pour « ${gmailState.filter.query} »`;
    if (localView === "later") return "Rien à traiter.";
    if (localView === "snoozed") return "Rien en attente.";
    if (activeLabelId) {
      const lbl = labelsState.byId.get(activeLabelId);
      if (lbl) return `Rien dans « ${displayLabel(lbl.name)} »`;
    }
    switch (gmailState.filter.kind) {
      case "all": return "Aucun mail.";
      case "sent": return "Rien d'envoyé.";
      case "unread": return "Tout est lu.";
      default: return "Boîte vide.";
    }
  });

  const sw = $derived(settingsState.swipe);
  const GESTURES = [
    { pos: "left-short", gesture: "shortLeft" },
    { pos: "left-long", gesture: "longLeft" },
    { pos: "right-short", gesture: "shortRight" },
    { pos: "right-long", gesture: "longRight" },
  ] as const;

  const todayLabel = new Date().toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
  });

  // === Balayage configurable ==============================================
  // `swipeRow.ts` ne connaît que quatre POSITIONS ; c'est ici qu'une position
  // devient une action, d'après les réglages.
  const ACTION_ICON: Record<SwipeAction, IconName> = {
    archive: "archive",
    later: "star",
    snooze: "clock",
    read: "check",
    reply: "reply",
    label: "filter",
  };
  /** L'action retire-t-elle la ligne de la liste ? Décide du glissé. */
  const ACTION_EXITS: Record<SwipeAction, boolean> = {
    archive: true,
    later: true,
    snooze: true,
    read: false,
    reply: false,
    label: false,
  };

  function runAction(action: SwipeAction, row: InboxRow) {
    const id = row.thread.id;
    switch (action) {
      case "archive":
        void archiveThread(id);
        showToast({ message: "Archivé" });
        return;
      case "later":
        void toggleLater(id);
        showToast({
          message: isLater(id) ? "Marqué « À traiter »" : "Retiré de « À traiter »",
        });
        return;
      case "snooze":
        openSnooze(id);
        return;
      case "read":
        if (row.unread) void markRead(id);
        else void markUnread(id);
        return;
      case "reply": {
        const last = row.thread.messages[row.thread.messages.length - 1];
        if (last) openReplyCompose(last, id);
        return;
      }
      case "label":
        openLabelSheet(id);
        return;
    }
  }

  /** Libellé affiché sous le doigt — « Lu » ou « Non lu » selon l'état. */
  function actionLabel(action: SwipeAction, row: InboxRow): string {
    if (action === "read") return row.unread ? "Marquer lu" : "Marquer non lu";
    return SWIPE_ACTION_LABELS[action];
  }

  // === En-tête collant ====================================================
  // Deux barres collantes à `top: 0` se recouvrent : les en-têtes de groupe
  // disparaîtraient derrière le titre. On MESURE l'en-tête (sa hauteur varie
  // avec le titre, le mode sélection, le nombre de lentilles) et les groupes
  // se collent juste dessous.
  let headEl = $state<HTMLElement | undefined>();
  let headH = $state(0);

  $effect(() => {
    const el = headEl;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => (headH = el.offsetHeight));
    ro.observe(el);
    headH = el.offsetHeight;
    return () => ro.disconnect();
  });

  function onRowClick(row: InboxRow) {
    if (selectMode) {
      toggleSelected(row.thread.id);
      return;
    }
    gmailState.selectedThreadId = row.thread.id;
  }
</script>

<div class="inbox" style:--head-h="{headH}px">
  {#if gmailState.needsReauth}
    <div class="notice">
      <h3>Connecter Gmail</h3>
      <p>
        Tu dois ré-autoriser ton compte Google avec l'accès à Gmail pour lire ta
        boîte. Tes mails restent privés — Google demande ton consentement.
      </p>
      <button class="primary" onclick={startSignIn}>Re-connecter Gmail</button>
    </div>
  {:else if gmailState.error && gmailState.rows.length === 0}
    <div class="notice error">
      <p>{gmailState.error}</p>
      <button class="primary" onclick={() => loadInbox()}>Réessayer</button>
    </div>
  {:else}
    <header class="head" bind:this={headEl}>
      <div class="title-row">
        <div class="titles">
          <h1>{headerTitle}</h1>
          <div class="meta">
            <span>{todayLabel}</span>
            <span class="sep" aria-hidden="true"></span>
            {#if unreadCount > 0}
              <span class="unread"><span class="pip" aria-hidden="true"></span>{unreadCount} non lus</span>
              <span>· {visibleRows.length} fils</span>
            {:else}
              <span>{visibleRows.length} fils</span>
            {/if}
          </div>
        </div>
        <div class="head-actions">
          <button
            class="icon-btn"
            class:on={selectMode}
            onclick={toggleSelectMode}
            aria-label="Sélection multiple"
            aria-pressed={selectMode}
          >
            <Icon name="check" size={16} />
          </button>
          <button class="group-btn" onclick={cycleGrouping}>
            <Icon name="list" size={13} />
            {GROUP_LABELS[grouping]}
          </button>
        </div>
      </div>

      <button class="search-field" onclick={onSearch}>
        <Icon name="search" size={15} />
        <span class="ph">Rechercher — from:, has:, avant:…</span>
        <kbd>⌘K</kbd>
      </button>

      <nav class="lenses no-scrollbar" aria-label="Lentilles">
        {#each lenses as l (l.id)}
          <button class="lens" class:on={l.on} onclick={l.pick}>
            {l.label}
            {#if l.count > 0}<span class="n">{l.count}</span>{/if}
          </button>
        {/each}
      </nav>
    </header>

    {#if selectMode}
      <div class="selbar">
        <span class="count">{selected.size} sélectionné{selected.size > 1 ? "s" : ""}</span>
        <div class="acts">
          <button onclick={bulkArchive} disabled={selected.size === 0}>Archiver</button>
          <button onclick={bulkRead} disabled={selected.size === 0}>Marquer lu</button>
          <button class="cancel" onclick={toggleSelectMode}>Annuler</button>
        </div>
      </div>
    {/if}

    {#if gmailState.loading && gmailState.rows.length === 0}
      <ul class="rows" aria-busy="true">
        {#each Array(8) as _, i (i)}
          <li class="row skel">
            <span class="c-dot"></span>
            <span class="mfx-skel w-sender"></span>
            <span class="mfx-skel"></span>
            <span class="mfx-skel w-time"></span>
          </li>
        {/each}
      </ul>
    {:else if visibleRows.length === 0}
      <div class="empty"><p>{emptyMessage}</p></div>
    {:else}
      {#each groups as g, gi (g.key)}
        {#if grouping !== "none"}
          <div class="group-head">
            <span>{g.key}</span>
            <span class="n">{g.rows.length}</span>
          </div>
        {/if}
        <ul class="rows">
          {#each g.rows as row, ri (row.thread.id)}
            <li
              class="mfx-swipe"
              use:swipeRow={{
                enabled: !selectMode,
                onShortLeft: () => runAction(sw.shortLeft, row),
                onLongLeft: () => runAction(sw.longLeft, row),
                onShortRight: () => runAction(sw.shortRight, row),
                onLongRight: () => runAction(sw.longRight, row),
                onLongPress: () => runAction(sw.longPress, row),
                exits: {
                  shortLeft: ACTION_EXITS[sw.shortLeft],
                  longLeft: ACTION_EXITS[sw.longLeft],
                  shortRight: ACTION_EXITS[sw.shortRight],
                  longRight: ACTION_EXITS[sw.longRight],
                },
              }}
            >
              {#each GESTURES as g (g.pos)}
                <div class="mfx-swipe-act {g.pos}" aria-hidden="true">
                  <Icon name={ACTION_ICON[sw[g.gesture]]} size={16} />
                  <span>{actionLabel(sw[g.gesture], row)}</span>
                </div>
              {/each}

              <button
                class="row"
                class:sel={selected.has(row.thread.id)}
                data-row-content
                style:transform={peek && gi === 0 && ri === 0 ? "translateX(-84px)" : undefined}
                onclick={() => onRowClick(row)}
              >
                <span class="c-dot">
                  {#if selectMode}
                    <span class="box" class:checked={selected.has(row.thread.id)}>
                      {#if selected.has(row.thread.id)}<Icon name="check" size={9} />{/if}
                    </span>
                  {:else if row.unread}
                    <span class="dot" aria-label="Non lu"></span>
                  {/if}
                </span>

                <span class="sender" class:strong={row.unread}>{senderDisplay(row)}</span>
                <span class="subject" class:strong={row.unread}>{row.subject}</span>

                <span class="tail">
                  {#if row.thread.messages.length > 1}
                    <span class="msgs">{row.thread.messages.length}</span>
                  {/if}
                  {#if hasAttachment(row)}
                    <Icon name="paperclip" size={12} />
                  {/if}
                  <span class="time" class:accent={row.unread}>{relativeDate(row.lastDate)}</span>
                </span>
              </button>
            </li>
          {/each}
        </ul>
      {/each}

      {#if gmailState.nextPageToken}
        <div class="more-wrap">
          <button class="more" onclick={() => loadMore()} disabled={gmailState.loadingMore}>
            {gmailState.loadingMore ? "Chargement…" : "Charger les fils plus anciens"}
          </button>
        </div>
      {/if}

      {#if gmailState.error && gmailState.rows.length > 0}
        <p class="inline-err">{gmailState.error}</p>
      {/if}
    {/if}
  {/if}
</div>

<style>
  .inbox {
    background: var(--bg);
    color: var(--fg);
  }

  /* === En-tête ============================================================
     Collant, et les en-têtes de groupe se collent SOUS lui grâce à la
     hauteur mesurée (--head-h) : c'est ce qui permet aux deux de coexister
     dans un même défilement de page. */
  .head {
    position: sticky;
    top: 0;
    z-index: 20;
    padding: calc(env(safe-area-inset-top) + 14px) 0 0;
    background: color-mix(in srgb, var(--bg) 86%, transparent);
    backdrop-filter: saturate(180%) blur(20px);
    -webkit-backdrop-filter: saturate(180%) blur(20px);
    border-bottom: 1px solid var(--border-subtle);
  }
  .title-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 10px;
    padding: 0 16px 10px;
  }
  /* `min-width: 0` sinon la ligne méta impose sa largeur au flex et pousse
     les boutons hors de l'écran à 375px. */
  .titles { min-width: 0; }
  h1 {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    font-size: 31px;
    line-height: 1;
    letter-spacing: -0.015em;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 7px;
    white-space: nowrap;
    overflow: hidden;
    font-family: var(--font-mono);
    font-size: 9.5px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .sep {
    width: 1px;
    height: 9px;
    background: var(--border);
  }
  .unread {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--accent);
  }
  .pip {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }

  .head-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }
  .icon-btn {
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: var(--radius-md);
    color: var(--fg-muted);
    transition: var(--tr);
  }
  .icon-btn.on {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .group-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-btn);
    font-size: 11.5px;
    font-weight: 500;
    color: var(--fg-muted);
    white-space: nowrap;
    transition: var(--tr);
  }
  .group-btn:active { background: var(--bg-hover); }

  .search-field {
    display: flex;
    align-items: center;
    gap: 8px;
    width: calc(100% - 32px);
    margin: 0 16px 10px;
    height: 36px;
    padding: 0 11px;
    border: 1px solid var(--border-subtle);
    background: var(--bg-elev);
    border-radius: var(--radius-ctl);
    color: var(--fg-subtle);
  }
  .search-field .ph {
    flex: 1;
    text-align: left;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lenses {
    display: flex;
    gap: 6px;
    padding: 0 16px 10px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .lens {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    font-size: 12px;
    font-weight: 500;
    color: var(--fg-muted);
    white-space: nowrap;
    transition: var(--tr);
  }
  .lens.on {
    background: var(--accent-soft);
    border-color: var(--accent);
    color: var(--accent);
  }
  .lens .n {
    font-family: var(--font-mono);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    opacity: 0.7;
  }

  /* === Barre de sélection ================================================= */
  .selbar {
    position: sticky;
    top: var(--head-h, 0px);
    z-index: 19;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 9px 16px;
    background: var(--bg-elev);
    border-bottom: 1px solid var(--border-subtle);
  }
  .selbar .count {
    font-size: 12.5px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
  }
  .acts { display: flex; gap: 6px; }
  .acts button {
    height: 29px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius-btn);
    font-size: 12px;
    color: var(--fg);
  }
  .acts button:disabled { opacity: 0.4; }
  .acts .cancel { color: var(--bad); }

  /* === Groupes ============================================================ */
  .group-head {
    position: sticky;
    top: var(--head-h, 0px);
    z-index: 4;
    display: flex;
    justify-content: space-between;
    padding: 12px 16px 5px;
    background: color-mix(in srgb, var(--bg) 92%, transparent);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-weight: 500;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .group-head .n { opacity: 0.75; font-variant-numeric: tabular-nums; }

  /* === Lignes ============================================================= */
  .rows { list-style: none; margin: 0; padding: 0; }

  .row {
    position: relative;
    display: grid;
    grid-template-columns: 9px 88px minmax(0, 1fr) auto;
    column-gap: 9px;
    align-items: center;
    width: 100%;
    min-height: 46px;
    padding: 7px 14px 7px 16px;
    border-bottom: 1px solid var(--border-subtle);
    background: var(--bg);
    font-size: 13px;
    text-align: left;
    /* Le pan horizontal est géré en JS ; le vertical reste au navigateur. */
    touch-action: pan-y;
    transition: transform 220ms var(--ease-ios);
  }
  .row:active { background: var(--bg-hover); }
  .row.sel { background: var(--accent-soft); }

  /* Point et case à cocher partagent la MÊME cellule : passer en mode
     sélection ne décale pas la ligne d'un pixel. */
  .c-dot {
    grid-column: 1;
    display: grid;
    place-items: center;
  }
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
  }
  .box {
    width: 13px;
    height: 13px;
    border: 1px solid var(--border);
    border-radius: 3px;
    display: grid;
    place-items: center;
    color: var(--accent-fg);
  }
  .box.checked {
    background: var(--accent);
    border-color: var(--accent);
  }

  .sender {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--fg);
    font-weight: 450;
  }
  .sender.strong { font-weight: 600; }

  .subject {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--fg-muted);
    font-weight: 450;
  }
  .subject.strong {
    color: var(--fg);
    font-weight: 550;
  }

  .tail {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--fg-subtle);
  }
  .msgs {
    font-family: var(--font-mono);
    font-size: 9.5px;
    font-variant-numeric: tabular-nums;
    padding: 0 4px;
    border-radius: var(--radius-xs);
    background: var(--bg-hover);
  }
  .time {
    font-family: var(--font-mono);
    font-size: 10px;
    font-variant-numeric: tabular-nums;
    min-width: 23px;
    text-align: right;
  }
  .time.accent { color: var(--accent); }

  /* === Squelettes — même grille, même hauteur : rien ne saute ============= */
  .row.skel {
    pointer-events: none;
    display: grid;
    grid-template-columns: 9px 88px minmax(0, 1fr) auto;
    column-gap: 9px;
  }
  .row.skel .mfx-skel { margin: 4.5px 0; }
  .w-sender { width: 88px; }
  .w-time { width: 23px; }

  /* === Divers ============================================================= */
  .empty {
    padding: 4rem 2rem;
    text-align: center;
  }
  .empty p {
    margin: 0;
    font-family: var(--font-serif);
    font-style: italic;
    font-size: 22px;
    color: var(--fg-subtle);
  }

  .more-wrap { padding: 16px; }
  .more {
    display: block;
    width: 100%;
    height: 32px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-btn);
    font-size: 12px;
    color: var(--fg-muted);
  }
  .more:disabled { opacity: 0.5; }

  .notice {
    margin: 2rem 12px;
    padding: 20px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-xl);
    background: var(--bg-elev);
  }
  .notice h3 { margin: 0 0 8px; font-size: 15px; }
  .notice p { margin: 0 0 14px; font-size: 12.5px; color: var(--fg-muted); line-height: 1.5; }
  .notice.error p { color: var(--bad); }
  .primary {
    height: 38px;
    padding: 0 16px;
    border-radius: var(--radius-btn);
    background: var(--accent);
    color: var(--accent-fg);
    font-size: 13px;
    font-weight: 600;
  }

  .inline-err {
    margin: 10px 12px;
    padding: 10px 12px;
    border-radius: var(--radius-lg);
    background: var(--bad-bg);
    color: var(--bad);
    font-size: 12.5px;
  }
</style>
